/**
 * Frame23 – Distribute death certificates to institutions (IAD)
 */

import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft, ArrowRight, Building2, Plus, Trash2 } from 'lucide-react';
import { Button } from '../ui/Button';
import { SettlementLayout } from '../settlement/SettlementLayout';
import DocumentUploadSection from '../settlement/DocumentUploadSection';
import { useSettlementFrameData } from '../../hooks/useSettlementFrameData';
import { useDeathCertDocuments } from '../../hooks/useDeathCertDocuments';
import {
  DEATH_CERT_DOC,
  DEATH_CERT_FRAME_DATA,
  deathCertRoute,
} from '../../utils/deathCertWorkflow';

interface InstitutionRow {
  id: string;
  institution: string;
  contact: string;
  method: 'mail' | 'in_person' | 'portal' | '';
  dateSent: string;
  copiesProvided: number;
  confirmed: boolean;
}

function newId() {
  return `inst-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`;
}

function starterRows(): InstitutionRow[] {
  return [
    {
      id: newId(),
      institution: 'Primary bank / credit union',
      contact: '',
      method: 'mail',
      dateSent: '',
      copiesProvided: 1,
      confirmed: false,
    },
    {
      id: newId(),
      institution: 'Probate court (if filing)',
      contact: '',
      method: 'in_person',
      dateSent: '',
      copiesProvided: 1,
      confirmed: false,
    },
  ];
}

export default function Frame23() {
  const navigate = useNavigate();
  const { estateId } = useParams<{ estateId?: string }>();
  const { data, save, loading } = useSettlementFrameData(estateId ?? null, DEATH_CERT_FRAME_DATA.distribution);
  const { documents, onUpload, onDelete } = useDeathCertDocuments(estateId, 'death-cert-doc:23');

  const [rows, setRows] = useState<InstitutionRow[]>(starterRows);

  useEffect(() => {
    const raw = data.institutions;
    if (!Array.isArray(raw)) return;
    setRows(raw.length ? (raw as InstitutionRow[]) : []);
  }, [data.institutions]);

  const persist = async (next: InstitutionRow[]) => {
    setRows(next);
    await save({ institutions: next });
  };

  return (
    <SettlementLayout currentPhase="iad" currentStep="death-certificates" estateId={estateId ?? null}>
      <div className="max-w-4xl mx-auto">
        <div className="mb-8 flex items-start gap-3">
          <Building2 className="w-8 h-8 text-cyan-700 shrink-0 mt-1" />
          <div>
            <h1 className="text-3xl font-bold text-gray-900 mb-2">Distribute to Institutions</h1>
            <p className="text-gray-600">
              Track each institution that needs an original certified copy, how you sent it, and when you confirmed
              receipt.
            </p>
          </div>
        </div>

        <div className="bg-white rounded-lg border border-gray-200 shadow-sm overflow-hidden mb-6">
          <div className="overflow-x-auto">
            <table className="min-w-full text-sm">
              <thead className="bg-gray-50 text-gray-700 text-left">
                <tr>
                  <th className="px-4 py-3 font-semibold">Institution</th>
                  <th className="px-4 py-3 font-semibold">Contact / notes</th>
                  <th className="px-4 py-3 font-semibold w-36">Method</th>
                  <th className="px-4 py-3 font-semibold w-36">Date sent</th>
                  <th className="px-4 py-3 font-semibold w-24">Copies</th>
                  <th className="px-4 py-3 font-semibold w-28">Done</th>
                  <th className="w-10" />
                </tr>
              </thead>
              <tbody>
                {rows.map((row) => (
                  <tr key={row.id} className="border-t border-gray-100 align-top">
                    <td className="px-4 py-2">
                      <input
                        className="w-full rounded border border-gray-200 px-2 py-1.5"
                        value={row.institution}
                        onChange={(e) => {
                          const next = rows.map((r) =>
                            r.id === row.id ? { ...r, institution: e.target.value } : r
                          );
                          void persist(next);
                        }}
                      />
                    </td>
                    <td className="px-4 py-2">
                      <input
                        className="w-full rounded border border-gray-200 px-2 py-1.5"
                        value={row.contact}
                        onChange={(e) => {
                          const next = rows.map((r) => (r.id === row.id ? { ...r, contact: e.target.value } : r));
                          void persist(next);
                        }}
                        placeholder="Office, rep, tracking #"
                      />
                    </td>
                    <td className="px-4 py-2">
                      <select
                        className="w-full rounded border border-gray-200 px-2 py-1.5"
                        value={row.method}
                        onChange={(e) => {
                          const next = rows.map((r) =>
                            r.id === row.id ? { ...r, method: e.target.value as InstitutionRow['method'] } : r
                          );
                          void persist(next);
                        }}
                      >
                        <option value="">—</option>
                        <option value="mail">Certified mail / courier</option>
                        <option value="in_person">In person</option>
                        <option value="portal">Online portal</option>
                      </select>
                    </td>
                    <td className="px-4 py-2">
                      <input
                        type="date"
                        className="w-full rounded border border-gray-200 px-2 py-1.5"
                        value={row.dateSent}
                        onChange={(e) => {
                          const next = rows.map((r) => (r.id === row.id ? { ...r, dateSent: e.target.value } : r));
                          void persist(next);
                        }}
                      />
                    </td>
                    <td className="px-4 py-2">
                      <input
                        type="number"
                        min={1}
                        className="w-full rounded border border-gray-200 px-2 py-1.5"
                        value={row.copiesProvided}
                        onChange={(e) => {
                          const next = rows.map((r) =>
                            r.id === row.id ? { ...r, copiesProvided: Math.max(1, Number(e.target.value) || 1) } : r
                          );
                          void persist(next);
                        }}
                      />
                    </td>
                    <td className="px-4 py-2 text-center">
                      <input
                        type="checkbox"
                        checked={row.confirmed}
                        onChange={(e) => {
                          const next = rows.map((r) =>
                            r.id === row.id ? { ...r, confirmed: e.target.checked } : r
                          );
                          void persist(next);
                        }}
                      />
                    </td>
                    <td className="px-2 py-2">
                      <button
                        type="button"
                        className="text-gray-400 hover:text-red-600 p-1"
                        onClick={() => persist(rows.filter((r) => r.id !== row.id))}
                        aria-label="Remove institution"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div className="px-4 py-3 border-t border-gray-100 bg-gray-50">
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={() =>
                persist([
                  ...rows,
                  {
                    id: newId(),
                    institution: '',
                    contact: '',
                    method: '',
                    dateSent: '',
                    copiesProvided: 1,
                    confirmed: false,
                  },
                ])
              }
            >
              <Plus className="w-4 h-4 mr-1" />
              Add institution
            </Button>
          </div>
        </div>

        <div className="bg-white rounded-lg border border-gray-200 p-6 mb-8 shadow-sm">
          <DocumentUploadSection
            documents={documents}
            onUpload={async (file) => {
              await onUpload(file, DEATH_CERT_DOC.distributionProof);
            }}
            onDelete={(id) => onDelete(id)}
            disabled={!estateId}
          />
          <p className="text-xs text-gray-500 mt-2">
            Optional: mailing receipts, certified mail forms, or portal confirmations.
          </p>
        </div>

        <div className="flex justify-between">
          <Button variant="outline" onClick={() => navigate(deathCertRoute('/frame-22', estateId))} disabled={loading}>
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back
          </Button>
          <Button
            onClick={() => navigate(deathCertRoute('/frame-24', estateId))}
            size="lg"
            disabled={loading}
          >
            Workflow summary
            <ArrowRight className="w-5 h-5 ml-2" />
          </Button>
        </div>
      </div>
    </SettlementLayout>
  );
}
